import React, {
  useState,
  createContext,
  ReactElement,
  useMemo,
  useCallback,
  useContext,
} from 'react';

import Modal from 'components/Modal';
import Status from 'components/Status';

import {
  TModalContext,
  TModalProviderTypes,
  TOptions,
} from './ModalProvider.types';
import { DEFAULT_CONTENT } from './ModalProvider.constants';

export const ModalContext = createContext<TModalContext>((options) => options);

export function ModalProvider({
  children,
  type,
}: TModalProviderTypes): ReactElement {
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<TOptions>({
    type: type || 'error',
    title: '',
    description: '',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onCloseCallback: () => {},
  });

  const items = useCallback(
    (params) => {
      setVisible(true);
      setOptions({
        type: params?.type || 'error',
        title: params?.title || '',
        description: params?.description || '',
        onCloseCallback: params?.onCloseCallback,
        secondaryAction: params?.secondaryAction,
        primaryActionText: params?.primaryActionText,
        base: params?.base,
      });
    },
    [setVisible, setOptions]
  );

  function handleOnClose() {
    setVisible(false);
    options.onCloseCallback && options.onCloseCallback();
  }

  const value = useMemo(() => items, [items]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {visible && (
        <Modal
          visible={visible}
          base={options.base}
          onClose={handleOnClose}
          onPrimaryClick={handleOnClose}
          primaryActionText={options.primaryActionText}
          secondaryAction={options.secondaryAction}>
          <Status
            type={options.type}
            title={options.title || DEFAULT_CONTENT[options.type].title}
            description={
              options.description || DEFAULT_CONTENT[options.type].description
            }
          />
        </Modal>
      )}
    </ModalContext.Provider>
  );
}

export function useModal(): TModalContext {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
